import { useEffect, useRef, useState } from "react";
import { useInView } from "motion/react";

/**
 * Animates a number from 0 to `end` when it scrolls into view.
 * Accepts a formatted display string (e.g. "$3.2B+") and extracts the numeric part.
 */
export function CountUp({
	display,
	className,
}: {
	display: string;
	className?: string;
}) {
	const ref = useRef<HTMLSpanElement>(null);
	const inView = useInView(ref, { once: true, margin: "-40px" });
	const [rendered, setRendered] = useState(display.replace(/[\d.]+/, "0"));

	useEffect(() => {
		if (!inView) return;

		const match = display.match(/([\d.]+)/);
		if (!match) {
			setRendered(display);
			return;
		}

		const end = Number.parseFloat(match[1]);
		const startTime = performance.now();
		let raf: number;

		const tick = (now: number) => {
			const elapsed = (now - startTime) / 1000;
			const progress = Math.min(elapsed / 1.6, 1);
			// ease-out cubic
			const eased = 1 - (1 - progress) ** 3;
			const current = eased * end;

			// Preserve the same decimal precision as the display string
			const decimals = match[1].includes(".")
				? match[1].split(".")[1].length
				: 0;
			const formatted = current.toFixed(decimals);
			setRendered(display.replace(/[\d.]+/, formatted));

			if (progress < 1) {
				raf = requestAnimationFrame(tick);
			}
		};

		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [inView, display]);

	return (
		<span ref={ref} className={className}>
			{rendered}
		</span>
	);
}
